// ============================================================
//  src/admin/pages/AdminUsersPage.js
// ============================================================
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { listUsers } from '../api/adminClient';
import { PageHeader, Badge, Pagination } from '../components/UI';
import DataTable from '../components/DataTable';

const STATUS_LABEL = { 0: 'Unverified', 1: 'Active', 2: 'Suspended' };
const STATUS_TONE  = { 0: 'warning', 1: 'success', 2: 'danger' };

export default function AdminUsersPage() {
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const limit = 25;

  const load = useCallback(() => {
    setLoading(true);
    listUsers({ page, limit, search, status })
      .then(res => { setUsers(res.data.users); setTotal(res.data.total); })
      .catch(err => toast.error(err?.response?.data?.message || 'Could not load users'))
      .finally(() => setLoading(false));
  }, [page, search, status]);

  useEffect(() => { load(); }, [load]);

  const totalPages = Math.max(1, Math.ceil(total / limit));

  const columns = [
    { key: 'name', label: 'Name', primary: true, render: u => `${u.sFname} ${u.sLname}` },
    { key: 'email', label: 'Email', render: u => u.sEmail },
    { key: 'plan', label: 'Plan', render: u => u.sPlan || '—' },
    { key: 'bots', label: 'Bots', render: u => u.botCount },
    { key: 'status', label: 'Status', render: u => <Badge tone={STATUS_TONE[u.sStatus]}>{STATUS_LABEL[u.sStatus]}</Badge> },
    { key: 'joined', label: 'Joined', render: u => new Date((u.sCreatedAt || '').replace(' ', 'T')).toLocaleDateString() },
  ];

  return (
    <div className="bf-page">
      <PageHeader title="Users" subtitle={`${total.toLocaleString('en-NG')} registered subscribers`} />

      <div className="bf-toolbar">
        <input
          className="bf-input"
          placeholder="Search by name, email, phone…"
          value={search}
          onChange={e => { setSearch(e.target.value); setPage(1); }}
        />
        <select
          className="bf-select"
          value={status}
          onChange={e => { setStatus(e.target.value); setPage(1); }}
        >
          <option value="">All statuses</option>
          <option value="1">Active</option>
          <option value="0">Unverified</option>
          <option value="2">Suspended</option>
        </select>
      </div>

      <div className="bf-card">
        <div className="bf-card-body-flush">
          <DataTable
            columns={columns}
            rows={users}
            keyField="sId"
            onRowClick={u => navigate(`/bot-father/users/${u.sId}`)}
            loading={loading}
            loadingMessage="Loading users…"
            emptyMessage="No users found."
          />
        </div>
      </div>

      <Pagination page={page} totalPages={totalPages} onChange={setPage} total={total} limit={limit} />
    </div>
  );
}